import type { PinGeneratorConfig, PinStrength } from '../types';
import {
  DEFAULT_PIN_CONFIG,
  AMBIGUOUS_CHARACTERS,
  CHAR_SETS,
  PIN_STRENGTH_THRESHOLDS,
} from '../constants';

/**
 * Generate a cryptographically random PIN.
 * Uses the Web Crypto API (crypto.getRandomValues).
 */
export function generatePin(config: PinGeneratorConfig = DEFAULT_PIN_CONFIG): string {
  const { length, includeUppercase, includeLowercase, includeNumbers, includeSymbols } = config;

  let chars = '';
  if (includeNumbers) chars += CHAR_SETS.numbers;
  if (includeUppercase) chars += CHAR_SETS.uppercase;
  if (includeLowercase) chars += CHAR_SETS.lowercase;
  if (includeSymbols) chars += CHAR_SETS.symbols;

  // Fallback to numbers if nothing selected
  if (chars.length === 0) chars = CHAR_SETS.numbers;

  // Always exclude ambiguous characters
  chars = chars
    .split('')
    .filter((c: string) => !AMBIGUOUS_CHARACTERS.includes(c))
    .join('');

  if (chars.length === 0) {
    throw new Error('No characters available for PIN generation');
  }

  // Use rejection sampling to prevent modulo bias
  const result: string[] = [];
  const poolSize = chars.length;
  // Largest multiple of poolSize that fits in 256 (0-255)
  const maxValid = 256 - (256 % poolSize);
  const randomBuffer = new Uint8Array(1);

  while (result.length < length) {
    crypto.getRandomValues(randomBuffer);
    const value = randomBuffer[0];
    if (value < maxValid) {
      result.push(chars[value % poolSize]);
    }
  }

  return result.join('');
}

/**
 * Evaluate the strength of a PIN configuration.
 */
export function evaluatePinStrength(config: PinGeneratorConfig): PinStrength {
  let poolSize = 0;
  if (config.includeNumbers) poolSize += CHAR_SETS.numbers.length;
  if (config.includeUppercase) poolSize += CHAR_SETS.uppercase.length;
  if (config.includeLowercase) poolSize += CHAR_SETS.lowercase.length;
  if (config.includeSymbols) poolSize += CHAR_SETS.symbols.length;

  if (poolSize === 0) poolSize = CHAR_SETS.numbers.length;

  // Always exclude ambiguous characters
  const ambiguousInPool = AMBIGUOUS_CHARACTERS.split('').filter((c: string) => {
    if (config.includeNumbers && CHAR_SETS.numbers.includes(c)) return true;
    if (config.includeUppercase && CHAR_SETS.uppercase.includes(c)) return true;
    if (config.includeLowercase && CHAR_SETS.lowercase.includes(c)) return true;
    if (config.includeSymbols && CHAR_SETS.symbols.includes(c)) return true;
    return false;
  }).length;
  poolSize -= ambiguousInPool;

  const entropy = Math.log2(Math.pow(Math.max(poolSize, 1), config.length));

  if (entropy >= PIN_STRENGTH_THRESHOLDS.veryStrong) return 'Very Strong';
  if (entropy >= PIN_STRENGTH_THRESHOLDS.strong) return 'Strong';
  if (entropy >= PIN_STRENGTH_THRESHOLDS.fair) return 'Fair';
  return 'Weak';
}
